import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/probate", label: "Probate clearances", testid: "nav-probate" },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/areas", label: "Areas", testid: "nav-areas" },
  { to: "/reviews", label: "Reviews", testid: "nav-reviews" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/team", label: "Meet the team", testid: "nav-team" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#FFF5EC] border-b border-[#FFE1BF] shadow-sm">
      <div className="max-w-7xl mx-auto h-20 px-4 sm:px-6 lg:px-8 flex items-center gap-3 sm:gap-5">
        {/* ─── Left: logo + brand ─── */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0" data-testid="header-left-group">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-1.5 sm:gap-2 group min-w-0"
        >
          <img
            src={LOGO_URL}
            alt="Waites Clearances logo"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-contain bg-white border border-slate-200 shadow-sm shrink-0 group-hover:scale-105 transition-transform"
          />
          <span className="font-display font-black text-xl sm:text-2xl lg:text-3xl leading-none tracking-tight text-slate-900 whitespace-nowrap" data-testid="header-brand-name">
            Waites <span className="text-[#FF7F00]">Clearances</span>
          </span>
        </Link>
        </div>

        {/* ─── Right: hamburger ─── */}
        <div className="flex items-center gap-2 sm:gap-3 justify-end">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="nav-menu-btn"
                aria-label="Open menu"
                className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400 active:scale-[0.97] transition-all shrink-0"
              >
                <Menu className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white p-0 w-[88%] max-w-sm" data-testid="nav-sheet">
              <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-3 bg-[#FFF7EE]">
                <img
                  src={LOGO_URL}
                  alt="Waites Clearances logo"
                  className="h-12 w-12 rounded-xl object-contain bg-white border border-slate-200"
                />
                <div>
                  <p className="font-display font-extrabold text-slate-900 leading-tight">
                    Waites <span className="text-[#FF7F00]">Clearances</span>
                  </p>
                  <p className="text-xs text-slate-500">Family run in Peterborough</p>
                </div>
              </div>
              <nav className="p-4 flex flex-col gap-2" data-testid="nav-mobile">
                {NAV.map((n) => {
                  const active = pathname === n.to;
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setOpen(false)}
                      data-testid={`${n.testid}-mobile`}
                      className={`flex items-center justify-between px-5 py-4 rounded-2xl text-lg font-bold transition-colors ${
                        active
                          ? "bg-[#FFF3E6] text-[#B85800] border border-[#FFB870]"
                          : "text-slate-800 hover:bg-slate-50 border border-transparent"
                      }`}
                    >
                      <span>{n.label}</span>
                      <ArrowRight className={`h-5 w-5 ${active ? "text-[#FF7F00]" : "text-slate-400"}`} />
                    </Link>
                  );
                })}
                <Link
                  to="/book"
                  onClick={() => setOpen(false)}
                  data-testid="nav-book-mobile"
                  className="mt-4 flex items-center justify-center gap-2 px-5 py-4 rounded-2xl bg-[#FF7F00] hover:bg-[#E67000] text-white text-lg font-extrabold shadow-lg shadow-orange-200"
                >
                  Get a Free Quote <ArrowRight className="h-5 w-5" />
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
